/**
 * Helpers for opening / printing return-label images.
 *
 * Strategy:
 * - View: convert data: URL → blob: URL → open directly in a new tab.
 *   Browsers render image data natively in tabs, no wrapper page needed.
 * - Print: same blob URL embedded in a HIDDEN IFRAME inside the parent page
 *   (same origin = no popup-blocker / about:blank rendering issues),
 *   then call iframe.contentWindow.print().
 * - Download: vanilla anchor + download attribute (data: URLs are allowed here).
 *
 * Why not document.write into a popup? `window.open('', '_blank')` then
 * writing HTML works inconsistently across browsers — some render with a
 * black/dark default before our CSS lands, some block the script that
 * triggers print(), some kill the blob URL when the popup unloads. The
 * iframe approach sidesteps all of that.
 */

/** Resolve the best image src from a package object. */
export const resolveLabelSrc = (pkg) => {
  if (!pkg) return null;
  if (pkg.label_url?.startsWith('data:')) return pkg.label_url;
  if (pkg.qr_image?.startsWith('data:')) return pkg.qr_image;
  const raw = pkg.label_url || pkg.qr_image;
  if (!raw) return null;
  if (typeof raw !== 'string') return null;
  if (raw.startsWith('http')) return raw;
  return `data:image/png;base64,${raw}`;
};

/** data:image/...;base64,... → Blob */
const dataUrlToBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(',');
  const mime = (meta.match(/:(.*?);/) || [, 'image/png'])[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

/**
 * Convert any data: URL to a blob: URL.
 * Hosted URLs (http(s)) and existing blob URLs pass through unchanged.
 * Returns null when the src is missing/invalid.
 */
export const toViewableUrl = (src) => {
  if (!src || typeof src !== 'string') return null;
  if (!src.startsWith('data:')) return src;
  try {
    return URL.createObjectURL(dataUrlToBlob(src));
  } catch (e) {
    return null;
  }
};

/** Open the label/QR image in a new tab. Image renders natively. */
export const openLabelInNewTab = (src) => {
  const url = toViewableUrl(src);
  if (!url) {
    // eslint-disable-next-line no-alert
    alert('No label image is attached to this package yet.');
    return;
  }
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) {
    // eslint-disable-next-line no-alert
    alert('Your browser blocked the new tab. Please allow pop-ups for this site and try again.');
  }
};

/**
 * Print the label using a hidden iframe attached to the parent document.
 * Same-origin = no popup blocker, no document.write quirks, no black-tab issue.
 * The iframe self-removes after printing.
 */
export const printLabel = (src) => {
  const url = toViewableUrl(src);
  if (!url) {
    // eslint-disable-next-line no-alert
    alert('No label image is attached to this package yet.');
    return;
  }

  // Remove any prior print iframe.
  const previous = document.getElementById('rp-print-frame');
  if (previous) previous.remove();

  const iframe = document.createElement('iframe');
  iframe.id = 'rp-print-frame';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  // Wait for the image to load inside the iframe before triggering print.
  iframe.onload = () => {
    try {
      const win = iframe.contentWindow;
      win.focus();
      win.print();
    } catch (e) {
      // Fall back to opening in a new tab so the user can Ctrl+P themselves.
      openLabelInNewTab(src);
    }
    // Clean up after print dialog has had a moment to open.
    setTimeout(() => {
      iframe.remove();
    }, 1500);
  };

  // Use an HTML wrapper that maximises the image and applies print margins.
  const html = `<!DOCTYPE html>
<html>
<head>
<title>Return label</title>
<style>
  @page { margin: 0.5in; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .wrap { display: flex; justify-content: center; align-items: center; min-height: 100vh; }
  img { max-width: 100%; max-height: 100vh; display: block; }
</style>
</head>
<body>
<div class="wrap"><img src="${url}" alt="Return label" /></div>
</body>
</html>`;

  iframe.srcdoc = html;
};

/** Trigger a download of the label image. Works for data, blob, and hosted URLs. */
export const downloadLabel = (src, filename) => {
  if (!src) return;
  const link = document.createElement('a');
  link.href = src;
  link.download = filename || 'return-label.png';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
